import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'

// @MUI
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import VehicleHero from './VehicleComponents/VehicleHero'
import OverviewSection from './VehicleComponents/OverviewSection'
import DeploymentsSection from './VehicleComponents/DeploymentsSection'
import DefectsSection from './VehicleComponents/DefectsSection'
import ServicingSection from './VehicleComponents/ServicingSection'
import CleaningSection from './VehicleComponents/CleaningSection'
import SafetyChecksSection from './VehicleComponents/SafetyChecksSection'
import AssetsSection from './VehicleComponents/AssetsSection'
import PTSConfigSection from './VehicleComponents/PTSConfigSection'
import UnavailabilitySection, {
  isOverduePeriod,
} from './VehicleComponents/UnavailabilitySection'
import NotesSection from './VehicleComponents/NotesSection'
import FilesSection from './VehicleComponents/FilesSection'
import FuelSection from './VehicleComponents/FuelSection'
import UnavailabilityDialog from './VehicleComponents/UnavailabilityDialog'
import MakeReadyHistoryAccordion from '../makeReady/MakeReadyHistoryAccordion'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'
import { LOLER_STATE } from './VehicleComponents/Loler/lolerOptions'
import {
  FUEL_CARD_STATE,
  fuelCardsAlert,
} from './VehicleComponents/fuelCardStatus'

export const VehicleContext = createContext()

function useQuery() {
  const { search } = useLocation()
  return React.useMemo(() => new URLSearchParams(search), [search])
}

const TabLabel = ({ label, count, color }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <span>{label}</span>
    {count !== undefined && (
      <Chip
        size="small"
        label={count}
        color={color}
        sx={{ borderRadius: 1, height: 18, fontSize: '0.7rem' }}
      />
    )}
  </Stack>
)

const SingleVehicle = () => {
  const { fleetId } = useParams()
  const { tenantInfo } = useContext(GlobalContext)
  const {setPageTitle} = useDocumentTitle()
  const [loading, setLoading] = useState(true)
  const [vehicle, setVehicle] = useState({})
  const [dvlaData, setDvlaData] = useState({})
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [changesPresent, setChangesPresent] = useState(false)
  const [safetyCheckFrequency, setSafetyCheckFrequency] = useState(null)
  const [unavailabilityDialogOpen, setUnavailabilityDialogOpen] = useState(false)
  const [unavailability, setUnavailability] = useState([])
  const [unavailabilityLoading, setUnavailabilityLoading] = useState(true)
  const [unavailabilityError, setUnavailabilityError] = useState(false)

  const query = useQuery()
  const openTab = query.get('opentab')
  const [tab, setTab] = useState(
    openTab === 'cleans'
      ? 'cleaning'
      : openTab === 'safety'
        ? 'safety'
        : openTab === 'downtime'
          ? 'unavailability'
          : 'overview',
  )

  useEffect(() => {
    if (vehicle?.callSign) {
      setPageTitle(vehicle.callSign)
    }
  }, [vehicle, setPageTitle])

  const loadVehicle = () => {
    setLoading(true)
    API.get(`vehicle/${fleetId}`)
      .then((res) => {
        setVehicle(res.data.vehicle)
        setUpcomingEvents(res.data.upcomingEvents)
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  const loadUnavailability = () => {
    setUnavailabilityLoading(true)
    setUnavailabilityError(false)
    API.get(`/fleet/vehicles/${fleetId}/unavailability`)
      .then((res) => {
        if (res.status === 200) {
          setUnavailability(res.data || [])
        } else {
          setUnavailabilityError(true)
        }
      })
      .catch(() => setUnavailabilityError(true))
      .finally(() => setUnavailabilityLoading(false))
  }

  useEffect(() => {
    loadVehicle()
    loadUnavailability()
    API.get('/settings?select=vehicleSafetyCheckFrequency').then((res) => {
      if (res.status === 200) {
        setSafetyCheckFrequency(res.data?.vehicleSafetyCheckFrequency ?? null)
      }
    })
  }, [fleetId])

  useEffect(() => {
    if (vehicle.regPlate) {
      API.post('/dvla-lookup', { regPlate: vehicle.regPlate })
        .then((res) => {
          if (res.status === 200) {
            setDvlaData(res.data)
          }
        })
        .catch((e) => console.error(e))
    }
  }, [vehicle.regPlate])

  const safetyChecksEnabled =
    !!safetyCheckFrequency && safetyCheckFrequency !== 'Disabled'
  const hasPTS = Number(tenantInfo?.subscriptionTier) >= 3

  const tabs = useMemo(
    () =>
      [
        { key: 'overview', label: 'Overview' },
        { key: 'deployments', label: 'Deployments' },
        {
          key: 'defects',
          label: 'Defects',
          count: vehicle?.activeDefects ?? 0,
          color: (vehicle?.activeDefects ?? 0) > 0 ? 'error' : 'default',
        },
        { key: 'servicing', label: 'Servicing & MOT' },
        { key: 'cleaning', label: 'Cleaning' },
        {
          key: 'fuel',
          label: 'Fuel',
          // Expired / expiring fuel cards surface on the tab itself
          ...(fuelCardsAlert(vehicle?.fuelCards)
            ? {
                count: `Card ${FUEL_CARD_STATE[fuelCardsAlert(vehicle.fuelCards)].label.toLowerCase()}`,
                color: FUEL_CARD_STATE[fuelCardsAlert(vehicle.fuelCards)].tone,
              }
            : {}),
        },
        {
          key: 'safety',
          label: 'Safety Checks',
          // LOLER warnings surface on the tab itself
          ...(vehicle?.lolerStatus && vehicle.lolerStatus.state !== 'ok'
            ? {
                count: `LOLER ${LOLER_STATE[vehicle.lolerStatus.state]?.short ?? ''}`.trim(),
                color: LOLER_STATE[vehicle.lolerStatus.state]?.tone,
              }
            : {}),
        },
        {
          key: 'assets',
          label: 'Assets',
          count: vehicle?.assets?.length || undefined,
        },
        { key: 'makeready', label: 'Make Ready' },
        {
          key: 'unavailability',
          label: 'Planned Downtime',
          count: unavailability.length || undefined,
          color: unavailability.some(isOverduePeriod) ? 'error' : 'warning',
        },
        hasPTS && { key: 'pts', label: 'PTS' },
        {
          key: 'files',
          label: 'Files',
          count: vehicle?.files?.length || undefined,
        },
        { key: 'notes', label: 'Notes' },
      ].filter(Boolean),
    [vehicle, hasPTS, unavailability.length],
  )

  if (fetchError) {
    return (
      <Alert severity="error">
        Unable to load vehicle data. Please try again later
      </Alert>
    )
  }
  return loading ? (
    <LinearProgress />
  ) : (
    <VehicleContext.Provider
      value={{ vehicle, setVehicle, setChangesPresent, loadVehicle }}
    >
      <Stack spacing={2}>
        <VehicleHero
          dvlaData={dvlaData}
          onOpenSafety={() => setTab('safety')}
          safetyChecksEnabled={safetyChecksEnabled}
          onMarkUnavailable={() => setUnavailabilityDialogOpen(true)}
          unavailability={unavailability}
          onOpenDowntime={() => setTab('unavailability')}
        />
        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(e, value) => setTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{
              px: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              // A disabled scroll arrow keeps its 40px slot by default, which
              // left a gap before the first tab — collapse it entirely.
              '& .MuiTabs-scrollButtons.Mui-disabled': { display: 'none' },
            }}
          >
            {tabs.map((tabDef) => (
              <Tab
                key={tabDef.key}
                value={tabDef.key}
                label={
                  <TabLabel
                    label={tabDef.label}
                    count={tabDef.count}
                    color={tabDef.color}
                  />
                }
              />
            ))}
          </Tabs>
          <Box p={2}>
            {tab === 'overview' && (
              <OverviewSection
                dvlaData={dvlaData}
                upcomingEvents={upcomingEvents}
              />
            )}
            {tab === 'deployments' && (
              <DeploymentsSection onOpenServicing={() => setTab('servicing')} />
            )}
            {tab === 'defects' && <DefectsSection vehicleID={vehicle._id} />}
            {tab === 'servicing' && <ServicingSection />}
            {tab === 'cleaning' && <CleaningSection />}
            {tab === 'fuel' && <FuelSection />}
            {tab === 'safety' && (
              <SafetyChecksSection safetyChecksEnabled={safetyChecksEnabled} />
            )}
            {tab === 'assets' && <AssetsSection />}
            {tab === 'makeready' && (
              <MakeReadyHistoryAccordion
                assetOrVehicleId={vehicle._id}
                plain
              />
            )}
            {tab === 'unavailability' && (
              <UnavailabilitySection
                vehicleID={vehicle._id}
                periods={unavailability}
                loading={unavailabilityLoading}
                error={unavailabilityError}
                onChanged={loadUnavailability}
                onAdd={() => setUnavailabilityDialogOpen(true)}
              />
            )}
            {tab === 'pts' && <PTSConfigSection key={`pts-${vehicle._id}`} />}
            {tab === 'files' && <FilesSection />}
            {tab === 'notes' && <NotesSection vehicleID={vehicle._id} />}
          </Box>
        </Paper>
      </Stack>
      <UnavailabilityDialog
        open={unavailabilityDialogOpen}
        vehicleID={vehicle._id}
        period={null}
        onClose={() => setUnavailabilityDialogOpen(false)}
        onSaved={loadUnavailability}
      />
    </VehicleContext.Provider>
  )
}

export default SingleVehicle
